import { useRef, useState, useImperativeHandle, forwardRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import type { GoogleRecaptchaManagerRef } from "./types";

interface GoogleRecaptchaManagerProps {
  siteKey: string;
}

const GoogleRecaptchaManager = forwardRef<
  GoogleRecaptchaManagerRef,
  GoogleRecaptchaManagerProps
>(({ siteKey }, ref) => {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [visibleMode, setVisibleMode] = useState(false);
  const [pendingPromise, setPendingPromise] = useState<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);

  const handleCaptchaChange = (token: string | null) => {
    if (token && pendingPromise) {
      pendingPromise.resolve(token);
      setPendingPromise(null);
      setVisibleMode(false);
    } else if (!token && pendingPromise) {
      pendingPromise.reject(new Error("reCAPTCHA token is null"));
      setPendingPromise(null);
    }
  };

  const handleExpired = () => {
    recaptchaRef.current?.reset();
    if (pendingPromise) {
      pendingPromise.reject(new Error("reCAPTCHA expired"));
      setPendingPromise(null);
    }
  };

  const handleError = () => {
    recaptchaRef.current?.reset();
    if (pendingPromise) {
      pendingPromise.reject(new Error("reCAPTCHA error occurred"));
      setPendingPromise(null);
    }
  };

  useImperativeHandle(ref, () => ({
    execute: () => {
      return new Promise<string>((resolve, reject) => {
        if (visibleMode) {
          setPendingPromise({ resolve, reject });
        } else {
          recaptchaRef.current?.reset();
          recaptchaRef.current
            ?.executeAsync()
            .then((token) => {
              if (token) {
                resolve(token);
              } else {
                reject(new Error("Invisible reCAPTCHA token is null"));
              }
            })
            .catch(() => {
              recaptchaRef.current?.reset();
              setPendingPromise({ resolve, reject });
              setVisibleMode(true);
            });
        }
      });
    },
    reset: () => {
      recaptchaRef.current?.reset();
      setVisibleMode(false);
      if (pendingPromise) {
        pendingPromise.reject(new Error("reCAPTCHA reset"));
        setPendingPromise(null);
      }
    },
  }));

  return (
    <ReCAPTCHA
      sitekey={siteKey}
      ref={recaptchaRef}
      size={visibleMode ? "normal" : "invisible"}
      badge="bottomleft"
      onChange={handleCaptchaChange}
      onErrored={handleError}
      onExpired={handleExpired}
    />
  );
});

GoogleRecaptchaManager.displayName = "GoogleRecaptchaManager";

export default GoogleRecaptchaManager;
